import { logger } from '@hatchd/utils';
import { PlanDataTypeEnum, SurveysApi, SurveySpec } from 'api/client';
import Intro from 'components/intro';
import Layout from 'components/layout';
import SEO from 'components/seo';
import SwitchTheme from 'components/switch-theme';
import useAuth from 'hooks/use-auth';
import { GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import AnalyticsService, {
  logStartSurvey,
  ScreenNames,
} from 'services/analytics';
import { useStore } from 'store';
import { backgroundImages } from 'utils/config';
import { scrollToTop } from 'utils/helpers';
import APP_ROUTES from 'utils/routes';

const PAGE_TITLE = 'Are You Prepared to Stay and Defend?';
const PAGE_DESCRIPTION =
  'Being in a bushfire could be the most traumatic experience of your life. Make sure this is the right fire emergency plan for you and your household.';

interface Props {
  surveySpec: SurveySpec;
}

const SurveyIntro: NextPage<Props> = ({ surveySpec }) => {
  const { startAnonymousSession, isAuthenticated, loading } = useAuth();
  const [
    set,
    currentSurveySpec,
    updateSurveySpec,
    clearSurveyData,
    surveyDraft,
  ] = useStore((state) => [
    state.set,
    state.survey.spec,
    state.updateSurveySpec,
    state.clearSurveyData,
    state.survey.draft,
  ]);
  const router = useRouter();

  const clearPlanData = useStore((state) => state.clearPlanData);

  const onStart = useCallback(async () => {
    try {
      // Start an anonymous session unless we already have a current session.
      if (!loading && !isAuthenticated) await startAnonymousSession();

      clearPlanData();

      // Check if the spec version at build time differs from the latest spec from the API
      // if yes, update the stored version...
      const SurveyApi = new SurveysApi();
      const { data: latestSpec } = await SurveyApi.getSurveySpec();

      let currentVersion = currentSurveySpec?.version ?? surveySpec.version;
      if (latestSpec.version !== currentVersion) {
        logger.warn('Survey version mismatch! Updating specs locally...');
        updateSurveySpec(latestSpec);
        currentVersion = latestSpec.version;
      }

      set(({ survey }) => {
        survey.draft.version = currentVersion;
      }, 'updateSurveyDraft');

      set(({ plan }) => {
        plan.draft.type = PlanDataTypeEnum.Stay;
        plan.isDirty = true;
      }, 'setPlanType');

      logStartSurvey();

      // Kick off the survey...
      router.push(APP_ROUTES.stay.survey).then(scrollToTop);
    } catch (error) {
      // TODO: What should happen here?
      logger.error('Error fetching the survey spec:', error);
    }
  }, [
    loading,
    isAuthenticated,
    startAnonymousSession,
    clearPlanData,
    currentSurveySpec?.version,
    surveySpec.version,
    set,
    router,
    updateSurveySpec,
  ]);

  useEffect(() => {
    // Reset the survey data at the beginning of the survey if there is data.
    const { location } = surveyDraft;
    if (location?.postcode || location?.suburb) {
      clearSurveyData();
    }
  }, [clearSurveyData, surveyDraft]);

  useEffect(() => {
    if (!currentSurveySpec) updateSurveySpec(surveySpec);
  }, [surveySpec, updateSurveySpec, currentSurveySpec]);

  useEffectOnce(() => {
    AnalyticsService.trackScreen(ScreenNames.surveyIntro);
  });

  return (
    <SwitchTheme to='light'>
      <Layout mainBackground={backgroundImages.treesBurn}>
        <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        <Intro type={PlanDataTypeEnum.Stay} onStart={onStart} />
      </Layout>
    </SwitchTheme>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const SurveyApi = new SurveysApi();
  const { data: surveySpec } = await SurveyApi.getSurveySpec();

  return {
    props: { surveySpec },
  };
};

export default SurveyIntro;
